import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  BarChart3,
  RotateCcw,
  Eye,
  RefreshCw,
  File,
  ArrowLeft,
  Download,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { clientsService } from '@/services/api/clients';
import { DOCUMENT_CATEGORY_LABELS, formatFileSize } from '@/types/client';
import type { FailedFile } from '@/types/client';

type ProcessingPhase = 'idle' | 'processing' | 'complete';

interface MetadataExtractionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  onProcessingComplete?: () => void;
}

// ============= Failed Files Dialog =============

function FailedFilesDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  onRetrySuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  onRetrySuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const [retryingFileId, setRetryingFileId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FailedFile | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { data: failedFiles, isLoading, refetch } = useQuery({
    queryKey: ['failed-files', clientId],
    queryFn: () => clientsService.getFailedFiles(clientId),
    enabled: open && !!clientId,
  });

  const retryMutation = useMutation({
    mutationFn: (fileId: string) => clientsService.retryFailedFile(fileId, clientId),
    onSuccess: (_data, fileId) => {
      toast.success('File queued for re-analysis');
      setRetryingFileId(null);
      refetch();
      onRetrySuccess();
      queryClient.invalidateQueries({ queryKey: ['metadata-stats', clientId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Retry failed');
      setRetryingFileId(null);
    },
  });

  const retryAllMutation = useMutation({
    mutationFn: async () => {
      if (!failedFiles) return;
      for (const file of failedFiles) {
        await clientsService.retryFailedFile(file.id, clientId);
      }
    },
    onSuccess: () => {
      toast.success('All failed files queued for re-analysis');
      refetch();
      onRetrySuccess();
      queryClient.invalidateQueries({ queryKey: ['metadata-stats', clientId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Retry all failed');
    },
  });

  const handleRetry = (fileId: string) => {
    setRetryingFileId(fileId);
    retryMutation.mutate(fileId);
  };

  const handlePreview = async (file: FailedFile) => {
    try {
      setDownloading(true);
      const data = await clientsService.getFileDownloadUrl(file.id);
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      toast.error('Failed to open file preview');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Failed Files
          </DialogTitle>
          <DialogDescription>
            {failedFiles?.length || 0} file{(failedFiles?.length || 0) !== 1 ? 's' : ''} failed analysis for <strong>{clientName}</strong>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !failedFiles || failedFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm text-muted-foreground">No failed files</p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {failedFiles.map((file) => (
                <div
                  key={file.id}
                  className="border rounded-lg p-3 space-y-2"
                >
                  {/* File info row */}
                  <div className="flex items-start gap-3">
                    <File className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={file.filename}>
                        {file.filename}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(file.fileSize)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(file.updatedAt).toLocaleDateString('en-AU', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handlePreview(file)}
                        disabled={downloading}
                        title="Open file"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-primary"
                        onClick={() => handleRetry(file.id)}
                        disabled={retryingFileId === file.id || retryMutation.isPending}
                        title="Re-analyze this file"
                      >
                        {retryingFileId === file.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Error reason */}
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
                    <p className="text-xs text-red-700 dark:text-red-400 leading-relaxed">
                      {file.error}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2 border-t">
          {failedFiles && failedFiles.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => retryAllMutation.mutate()}
              disabled={retryAllMutation.isPending}
              className="mr-auto"
            >
              {retryAllMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
              )}
              Re-analyze All ({failedFiles.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= Main Dialog =============

export function MetadataExtractionDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  onProcessingComplete,
}: MetadataExtractionDialogProps) {
  const [phase, setPhase] = useState<ProcessingPhase>('idle');
  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const prevPendingRef = useRef<number | null>(null);

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['metadata-stats', clientId],
    queryFn: () => clientsService.getMetadataStats(clientId),
    enabled: open && !!clientId,
    refetchInterval: phase === 'processing' ? 3000 : false,
  });

  // Detect when async processing completes by watching pending count drop to 0
  useEffect(() => {
    if (phase !== 'processing' || !stats) return;

    const currentPending = (stats.pending || 0) + (stats.processing || 0);

    if (prevPendingRef.current !== null && currentPending === 0) {
      // All files processed
      setPhase('complete');
      onProcessingComplete?.();
      toast.success('File analysis complete');
    }

    prevPendingRef.current = currentPending;
  }, [stats, phase, onProcessingComplete]);

  const reindexMutation = useMutation({
    mutationFn: () => clientsService.reindexFiles(clientId),
    onSuccess: (result) => {
      toast.success(`${result.reset} files reset for re-indexing`);
      setPhase('idle');
      prevPendingRef.current = null;
      refetchStats();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Re-index failed');
    },
  });

  const startMutation = useMutation({
    mutationFn: () => clientsService.extractMetadata(clientId),
    onSuccess: () => {
      // POST returned 202 - async processing started in background
      // Stats polling will track progress
      toast.info('Analysis started in background');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start processing');
      setPhase('idle');
    },
  });

  const handleStart = () => {
    prevPendingRef.current = null;
    setPhase('processing');
    startMutation.mutate();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && phase === 'processing') {
      // Keep processing in background, just close dialog
      setPhase('idle');
      prevPendingRef.current = null;
    }
    onOpenChange(isOpen);
  };

  const total = stats?.total || 0;
  const completed = stats?.completed || 0;
  const skipped = stats?.skipped || 0;
  const pending = stats?.pending || 0;
  const processing = stats?.processing || 0;
  const failed = stats?.failed || 0;
  const progressPercent = total > 0 ? Math.round(((completed + skipped) / total) * 100) : 0;

  const categoryEntries = Object.entries(stats?.categories || {})
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  // Auto-detect if background processing is already running (e.g. dialog reopened)
  const isBackgroundProcessing = processing > 0;
  useEffect(() => {
    if (isBackgroundProcessing && phase === 'idle') {
      setPhase('processing');
    }
  }, [isBackgroundProcessing, phase]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Index & Analyze Files</DialogTitle>
            <DialogDescription>
              Extract metadata, classify documents, and generate summaries for <strong>{clientName}</strong>'s files.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stats Grid */}
            {statsLoading ? (
              <div className="flex items-center justify-center h-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg border">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Total Files</p>
                      <p className="text-lg font-semibold">{total}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-xs text-muted-foreground">Analyzed</p>
                      <p className="text-lg font-semibold text-green-600">{completed}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border">
                    <Clock className="h-5 w-5 text-amber-500" />
                    <div>
                      <p className="text-xs text-muted-foreground">Pending</p>
                      <p className="text-lg font-semibold text-amber-600">{pending + processing}</p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg border ${failed > 0 ? 'cursor-pointer hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-950/10 transition-colors' : ''}`}
                    onClick={() => failed > 0 && setFailedDialogOpen(true)}
                    role={failed > 0 ? 'button' : undefined}
                    tabIndex={failed > 0 ? 0 : undefined}
                  >
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Failed</p>
                      <p className="text-lg font-semibold text-red-600">{failed}</p>
                    </div>
                    {failed > 0 && (
                      <Eye className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{phase === 'processing' ? 'Processing...' : 'Progress'}</span>
                    <span>{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-2" />
                </div>

                {/* Category Breakdown */}
                {categoryEntries.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <BarChart3 className="h-4 w-4" />
                      Categories
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {categoryEntries.map(([category, count]) => (
                        <Badge key={category} variant="secondary" className="text-xs">
                          {DOCUMENT_CATEGORY_LABELS[category] || category} ({count})
                        </Badge>
                      ))}
                    </div>
                    {(stats?.scannedCount || 0) > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {stats!.scannedCount} scanned document{stats!.scannedCount !== 1 ? 's' : ''} processed with OCR
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {phase === 'idle' && completed > 0 && (
              <Button
                variant="outline"
                onClick={() => reindexMutation.mutate()}
                disabled={reindexMutation.isPending}
                className="mr-auto"
              >
                {reindexMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Re-index All
              </Button>
            )}
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {phase === 'complete' ? 'Close' : 'Cancel'}
            </Button>
            {phase === 'processing' ? (
              <Button disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing...
              </Button>
            ) : (pending + processing) > 0 ? (
              <Button onClick={handleStart} disabled={startMutation.isPending}>
                {startMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Start Analysis
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Failed Files Dialog - layered on top */}
      <FailedFilesDialog
        open={failedDialogOpen}
        onOpenChange={setFailedDialogOpen}
        clientId={clientId}
        clientName={clientName}
        onRetrySuccess={() => refetchStats()}
      />
    </>
  );
}
